import React, { useEffect, useRef } from 'react';
import type { AnyMessage } from './chatTypes';
import { uuidv4 } from '../../util';
import { isSameDay, isToday } from 'date-fns';
import { DefaultDateFormatter, intl } from '../../lib';
import { Divider } from './Divider';
import { VBox } from '../layout';
import type { ChatOptions } from './ChatMessageComponents/ChatMessageComponentsTypes';
import ChatMessageComponent from './ChatMessageComponents/ChatMessageComponent';

export interface ChatWindowProps {
  messages: AnyMessage[];
  chatInputComponent: JSX.Element;
  emptyStatePanel?: JSX.Element;
  chatOptions?: ChatOptions;
  className?: string;
}

export function ChatWindow({
  messages,
  chatInputComponent,
  chatOptions,
  emptyStatePanel,
  className = '',
  ...props
}: Readonly<ChatWindowProps>): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [scrollRef, messages]);

  return (
    <VBox {...props} className={`ehs-chat-window ${className}`.trim()}>
      <div
        className={
          messages.length === 0
            ? 'no-messages chat-window-body'
            : 'chat-window-body'
        }
      >
        {messages.length
          ? messages.map((message, index, messages) => (
              <React.Fragment key={uuidv4()}>
                {chatOptions?.showDateDivider !== false && (
                  <DateDivider messages={messages} currentIndex={index} />
                )}
                <ChatMessageComponent
                  message={message}
                  headerOptions={chatOptions?.headerOptions}
                />
              </React.Fragment>
            ))
          : emptyStatePanel}
        <div className="scroll-div" ref={scrollRef} />
      </div>
      <div className="chat-window-input">{chatInputComponent}</div>
    </VBox>
  );
}

function DateDivider({
  currentIndex,
  messages
}: {
  messages: AnyMessage[];
  currentIndex: number;
}): JSX.Element {
  if (currentIndex === 0 || messages.length <= 1) {
    return <></>;
  }
  const currentDate = messages[currentIndex].date;
  const previousDate = messages[currentIndex - 1].date;
  if (isSameDay(currentDate, previousDate)) {
    return <></>;
  }

  if (isToday(currentDate)) {
    const capitalizeFirstLetter = (word: string): string => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    };
    return <Divider text={capitalizeFirstLetter(intl('common.today'))} />;
  }

  return <Divider text={DefaultDateFormatter.formatDate(currentDate)} />;
}
